import { useEffect, useRef, useState } from "react";
import { motion, useSpring, useInView } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 40, className = "", once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function MaskedLines({ lines, className = "", lineClassName = "", delay = 0, as: Tag = "h1" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const MotionTag = motion[Tag] || motion.div;
  return (
    <MotionTag ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            initial={{ y: "110%" }}
            animate={inView ? { y: "0%" } : {}}
            transition={{ duration: 1, delay: delay + i * 0.12, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </MotionTag>
  );
}

export function Marquee({ items, dark = false, className = "" }) {
  const row = (
    <>
      {items.map((t, i) => (
        <span key={i} className="inline-flex items-center gap-8 pr-8 shrink-0">
          <span className="font-display italic text-2xl md:text-4xl tracking-tight">{t}</span>
          <span className="w-2 h-2 rounded-full bg-brand inline-block" />
        </span>
      ))}
    </>
  );
  return (
    <div
      data-testid="editorial-marquee"
      className={`overflow-hidden py-6 border-y ${
        dark ? "bg-ink text-paper/70 border-paper/10" : "bg-linen text-ink/60 border-ink/10"
      } ${className}`}
    >
      <div className="flex whitespace-nowrap animate-marquee w-max">
        <div className="flex">{row}</div>
        <div className="flex" aria-hidden="true">{row}</div>
      </div>
    </div>
  );
}

export function Magnetic({ children, strength = 0.3 }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  return (
    <motion.span
      ref={ref}
      className="inline-block"
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        setOffset({
          x: (e.clientX - r.left - r.width / 2) * strength,
          y: (e.clientY - r.top - r.height / 2) * strength,
        });
      }}
      onMouseLeave={() => setOffset({ x: 0, y: 0 })}
      animate={{ x: offset.x, y: offset.y }}
      transition={{ type: "spring", stiffness: 180, damping: 16, mass: 0.4 }}
    >
      {children}
    </motion.span>
  );
}

export function CursorDot() {
  const [visible, setVisible] = useState(false);
  const [hovering, setHovering] = useState(false);
  const x = useSpring(-100, { stiffness: 500, damping: 40, mass: 0.3 });
  const y = useSpring(-100, { stiffness: 500, damping: 40, mass: 0.3 });

  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setVisible(true);
      const t = e.target;
      setHovering(!!t.closest("a, button, [role='button'], input, select, textarea"));
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [x, y]);

  if (typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches) return null;

  return (
    <motion.div
      data-testid="cursor-dot"
      className="fixed top-0 left-0 z-[100] pointer-events-none hidden lg:block"
      style={{ x, y, opacity: visible ? 1 : 0 }}
    >
      <motion.div
        className="rounded-full bg-brand -translate-x-1/2 -translate-y-1/2"
        animate={{ width: hovering ? 22 : 8, height: hovering ? 22 : 8, opacity: hovering ? 0.55 : 1 }}
        transition={{ duration: 0.25 }}
      />
    </motion.div>
  );
}

export function Overline({ children, dark = false }) {
  return (
    <p className={`font-mono text-xs uppercase tracking-[0.25em] flex items-center gap-3 ${dark ? "text-brand" : "text-brand"}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-brand inline-block" />
      {children}
    </p>
  );
}
